import { Button, Divider, Drawer, Flex, Text } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { updateKycWorkflowKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { useKycWorkflowCustomFields } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycWorkflowCustomFields.ts';
import { ToggleRequirementItem } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/components/ToggleRequirementItem.tsx';
import { KycSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles.ts';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader.tsx';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import { ProfileDataRequirementsFormValues } from './ProfileDataRequirementsDrawer.types.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	kycWorkflowId: string | undefined;
}

const EMPTY_VALUES: ProfileDataRequirementsFormValues = {
	isPlaceOfBirthRequired: false,
	isCountryOfBirthRequired: false,
	isEmployeeIdRequired: false,
	isCitizenshipRequired: false,
	isTaxResidenceRequired: false,
	isTinRequired: false,
};

export const ProfileDataRequirementsDrawer = ({ opened, onClose, kycWorkflowId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const { data: kycWorkflowSettings, isLoading } = useKycWorkflowKycSetting(kycWorkflowId ?? '');
	const { individualCustomFields, vehicleCustomFields, toggleCustomField } =
		useKycWorkflowCustomFields(kycWorkflowId);
	const customFields = [...individualCustomFields, ...vehicleCustomFields];

	const form = useForm<ProfileDataRequirementsFormValues>({ defaultValues: EMPTY_VALUES });

	const updateMutation = useMutation({
		mutationFn: (data: ProfileDataRequirementsFormValues) =>
			updateKycWorkflowKycSetting(kycWorkflowId ?? '', data),
		onSuccess: async () => {
			await queryClient.refetchQueries({
				queryKey: [API_PATHS.KYC_SETTING_KYCWORKFLOW_KYCWORKFLOWID_GET, kycWorkflowId],
			});
			showToastV2({
				title: tMipManage('profile_data_requirements.update_success'),
				state: 'success',
			});
			onClose();
		},
		onError: () => {
			showToastV2({
				title: tMipManage('profile_data_requirements.update_error'),
				state: 'error',
			});
		},
	});

	useEffect(() => {
		if (!opened || !kycWorkflowSettings) {
			return;
		}

		form.reset({
			isPlaceOfBirthRequired: kycWorkflowSettings.isPlaceOfBirthRequired,
			isCountryOfBirthRequired: kycWorkflowSettings.isCountryOfBirthRequired,
			isEmployeeIdRequired: kycWorkflowSettings.isEmployeeIdRequired,
			isCitizenshipRequired: kycWorkflowSettings.isCitizenshipRequired,
			isTaxResidenceRequired: kycWorkflowSettings.isTaxResidenceRequired,
			isTinRequired: kycWorkflowSettings.isTinRequired,
		});
	}, [opened, kycWorkflowSettings, form]);

	const taxResidenceChecked = form.watch('isTaxResidenceRequired');
	const placeOfBirthChecked = form.watch('isPlaceOfBirthRequired');
	const countryOfBirthChecked = form.watch('isCountryOfBirthRequired');
	const employeeIdChecked = form.watch('isEmployeeIdRequired');
	const citizenshipChecked = form.watch('isCitizenshipRequired');
	const tinChecked = form.watch('isTinRequired');

	const onSubmit = (values: ProfileDataRequirementsFormValues) => {
		if (!kycWorkflowId) {
			return;
		}
		updateMutation.mutate({
			...values,
			isTinRequired: values.isTaxResidenceRequired ? values.isTinRequired : false,
		});
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('profile_data_requirements.title')}
			styles={{
				header: {
					paddingBottom: '0.5rem',
				},
			}}
		>
			<FormProvider {...form}>
				<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
					<Flex direction="column" h="100%">
						<Text c="dark.0">{tMipManage('profile_data_requirements.subtitle')}</Text>

						<Divider mt="2rem" mb="1.25rem" />

						<Text fz="sm" fw={600} c="dark.0" tt="uppercase" mb="0.75rem">
							{tMipManage('profile_data_requirements.personal_details')}
						</Text>

						{isLoading ? (
							<InlineLoader />
						) : (
							<KycSection $noMaxWidth $noPadding $noGap>
								<Flex direction="column" gap="1rem">
									<ToggleRequirementItem
										title={tMipManage('investor_data.place_of_birth.label')}
										description={tMipManage(
											'investor_data.place_of_birth.description'
										)}
										checked={placeOfBirthChecked}
										onToggle={() =>
											form.setValue(
												'isPlaceOfBirthRequired',
												!placeOfBirthChecked
											)
										}
										showBorder={false}
									/>

									<ToggleRequirementItem
										title={tMipManage('investor_data.country_of_birth.label', {
											defaultValue: 'Country of birth',
										})}
										description={tMipManage(
											'investor_data.country_of_birth.description',
											{
												defaultValue:
													'Require investors to provide their country of birth',
											}
										)}
										checked={countryOfBirthChecked}
										onToggle={() =>
											form.setValue(
												'isCountryOfBirthRequired',
												!countryOfBirthChecked
											)
										}
										showBorder={false}
									/>
								</Flex>

								<ToggleRequirementItem
									title={tMipManage('investor_data.employee_id.label')}
									description={tMipManage(
										'investor_data.employee_id.description'
									)}
									checked={employeeIdChecked}
									onToggle={() =>
										form.setValue('isEmployeeIdRequired', !employeeIdChecked)
									}
									showBorder={false}
								/>

								<ToggleRequirementItem
									title={tMipManage('investor_data.citizenship.label')}
									description={tMipManage(
										'investor_data.citizenship.description'
									)}
									checked={citizenshipChecked}
									onToggle={() =>
										form.setValue('isCitizenshipRequired', !citizenshipChecked)
									}
									showBorder={false}
								/>

								<Text
									fz="sm"
									fw={600}
									c="dark.0"
									tt="uppercase"
									mt="0.75rem"
									mb="0.75rem"
								>
									{tMipManage('profile_data_requirements.tax_information')}
								</Text>

								<Flex direction="column" gap="1rem">
									<ToggleRequirementItem
										title={tMipManage('investor_data.tax_residence.label')}
										description={tMipManage(
											'investor_data.tax_residence.description'
										)}
										checked={taxResidenceChecked}
										onToggle={() => {
											const next = !taxResidenceChecked;
											form.setValue('isTaxResidenceRequired', next);
											if (!next) {
												form.setValue('isTinRequired', false);
											}
										}}
										showBorder={false}
									/>

									<ToggleRequirementItem
										title={tMipManage('investor_data.tin.label')}
										description={tMipManage('investor_data.tin.description')}
										checked={tinChecked}
										disabled={!taxResidenceChecked}
										onToggle={() => form.setValue('isTinRequired', !tinChecked)}
										showBorder={false}
									/>
								</Flex>

								{customFields.length > 0 && (
									<>
										<Text
											fz="sm"
											fw={600}
											c="dark.0"
											tt="uppercase"
											mt="0.75rem"
											mb="0.75rem"
										>
											{tMipManage(
												'investor_data.custom_fields.section_title'
											)}
										</Text>
										<Flex direction="column" gap="1rem">
											{customFields.map((field) => (
												<ToggleRequirementItem
													key={field.customDetailId}
													title={field.name}
													description={field.instruction ?? ''}
													checked={field.isRequired}
													onToggle={() =>
														toggleCustomField(
															field.customDetailId,
															field.isRequired
														)
													}
													showBorder={false}
												/>
											))}
										</Flex>
									</>
								)}
							</KycSection>
						)}

						<Button
							fullWidth
							mt="auto"
							type="submit"
							loading={updateMutation.isPending}
							disabled={!kycWorkflowId}
							fw={600}
						>
							{tMipManage('profile_data_requirements.submit')}
						</Button>

						<Button
							variant="subtle"
							fullWidth
							loading={updateMutation.isPending}
							c="dark.8"
							fw={600}
							onClick={onClose}
						>
							{tMipManage('profile_data_requirements.cancel')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
